<template>
  <PageChrome>
    <template #title>
      <span class="eyebrow">Dialer history</span>
      <span v-if="total > 0" class="totalBadge">{{ total }}</span>
    </template>

    <template #actions>
      <span v-if="shellOwnsTitle && total > 0" class="totalCount">{{ total }} {{ total === 1 ? 'contact' : 'contacts' }}</span>
      <SortMenu
        v-model="sortChoice"
        variant="label"
        :options="HISTORY_SORT_OPTIONS"
        :active="sortChoice !== DEFAULT_SORT_CHOICE"
      />
      <FilterMenu :active-count="activeFilterCount" @clear="clearFilters">
        <div v-if="hasPlays" class="field">
          <span class="fieldLabel">Sales play</span>
          <SearchSelect
            v-model="salesPlay"
            :options="salesPlayOptions"
            placeholder="All sales plays"
            search-placeholder="Search plays…"
            :allow-clear="true"
            clear-label="All sales plays"
          />
        </div>
        <div class="field">
          <span class="fieldLabel">Account</span>
          <!-- Async options: a large tenant has more dialed accounts than the dropdown
               will filter client-side, so the search goes to the server. -->
          <SearchSelect
            v-model="accountId"
            :options-function="searchAccounts"
            placeholder="All accounts"
            search-placeholder="Search accounts…"
            :allow-clear="true"
            clear-label="All accounts"
          />
        </div>
        <div class="field">
          <span class="fieldLabel">Rep</span>
          <SearchSelect
            v-model="repId"
            :options="repOptions"
            placeholder="All reps"
            search-placeholder="Search reps…"
            :allow-clear="true"
            clear-label="All reps"
          />
        </div>
        <ToggleSwitch v-model="connectedOnly" label="Connected only" />
      </FilterMenu>
      <button type="button" class="btn secondary openDialerBtn" @click="openDialer">
        <AppIcon name="phone_call" :size="15" />Open dialer
      </button>
    </template>

    <div v-if="loading && items.length === 0" class="empty"><span class="emptyText">Loading dialer history…</span></div>
    <div v-else-if="items.length === 0" class="empty">
      <span class="emptyText">No dialed contacts yet.</span>
      <!-- The account list is scoped to the play and rep picked before it, and the rep list
           is never scoped at all, so these combinations can legitimately have no rows. Say
           which filter to drop rather than leaving a dead end. -->
      <span v-if="accountId && salesPlay && hasPlays" class="emptyHint">
        No calls for this account in this sales play — clear the account filter.
      </span>
      <span v-else-if="repId && activeFilterCount > 1" class="emptyHint">
        No calls by this rep match the other filters — clear a filter.
      </span>
      <span v-else-if="repId && otherRepsHaveCalls" class="emptyHint">
        No calls by this rep — clear the rep filter to see the team’s calls.
      </span>
    </div>
    <div v-else class="histScroll">
      <div class="histTable" :class="{ noPlays: !hasPlays }">
      <div class="histHead">
        <span class="headSpacer" />
        <span class="colLabel">Contact</span>
        <span class="colLabel">Account</span>
        <span v-if="hasPlays" class="colLabel">Sales play</span>
        <span class="colLabel">Attempts</span>
        <span class="colLabel">Last disposition</span>
        <span class="colLabel">Last attempt by</span>
        <span class="colLabel">Last attempt at</span>
      </div>

      <div class="rows">
        <div v-for="item in items" :key="rowKey(item)" class="rowBlock">
          <DialerHistoryRow
            :item="item"
            :expanded="expandedKey === rowKey(item)"
            :has-plays="hasPlays"
            @toggle="onToggleRow(item)"
          />
          <DialerHistoryDetail
            v-if="expandedKey === rowKey(item)"
            :item="item"
          />
        </div>
        <div ref="sentinelEl" class="scrollSentinel" />
      </div>

      <div v-if="loading && items.length > 0" class="loadingMore">Loading…</div>
      </div>
    </div>
  </PageChrome>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { LocationQueryRaw } from 'vue-router'
import PageChrome from '../components/PageChrome.vue'
import AppIcon from '../../components/AppIcon.vue'
import SearchSelect from '../../components/SearchSelect.vue'
import ToggleSwitch from '../../components/ToggleSwitch.vue'
import DialerHistoryRow from '../components/dialer/DialerHistoryRow.vue'
import DialerHistoryDetail from '../components/dialer/DialerHistoryDetail.vue'
import FilterMenu from '../components/list/FilterMenu.vue'
import SortMenu from '../components/list/SortMenu.vue'
import {
  getDialerHistory,
  getDialerHistoryFilterOptions,
  type DialerHistoryItem,
  type DialerHistorySort,
  type FilterOption,
} from '../../services/dialerApi'
import { preloadCrmLinkConfig } from '../../services/crmLinks'
import { useContextDrawer } from '../../drawer/useContextDrawer'
import { DRAWER_CONTEXT_ACCOUNT, DRAWER_CONTEXT_CONTACT } from '../../drawer/drawerTypes'
import { useCurrentUser } from '../../services/user'
import { useShellOwnsTitle } from '../composables/shellTitle'

const router = useRouter()
const route = useRoute()
const shellOwnsTitle = useShellOwnsTitle()
const { setContext, openDrawer, closeDrawer } = useContextDrawer()
// Dialer-only tenants have no sales plays: no play filter, no play column.
const { me, isDialerOnly } = useCurrentUser()
const hasPlays = computed(() => !isDialerOnly.value)

const items = ref<DialerHistoryItem[]>([])
const total = ref(0)
const nextCursor = ref<string | null>(null)
const loading = ref(false)
const expandedKey = ref<string | null>(null)

// Sort + filters live in the URL (?sort ?play ?account ?rep ?connected) — the
// same URL-as-state contract as the queue rail (useDialerQueue) and the
// candidates pages, so history views are deep-linkable and back/forward works.
// Defaults are omitted from the query; unmanaged keys are preserved.
const queryStr = (key: string): string => {
  const raw = route.query[key]
  const value = Array.isArray(raw) ? raw[0] : raw
  return typeof value === 'string' ? value.trim() : ''
}

// Multi-field writes MUST go through one pushQuery call (like clearFilters):
// two sequential setter writes would each read the not-yet-navigated route
// and the second would drop the first.
function pushQuery(patch: {
  sort?: string
  play?: string
  account?: string
  rep?: string
  connected?: boolean
}): void {
  const next = {
    sort: patch.sort ?? sortChoice.value,
    play: patch.play ?? salesPlay.value,
    account: patch.account ?? accountId.value,
    rep: patch.rep ?? repId.value,
    connected: patch.connected ?? connectedOnly.value,
  }
  // Value-level no-op guard (key-order-proof): SearchSelect re-emits on
  // re-picking the same option, and a junk history entry costs a back press.
  if (
    next.sort === sortChoice.value &&
    next.play === salesPlay.value &&
    next.account === accountId.value &&
    next.rep === repId.value &&
    next.connected === connectedOnly.value
  ) {
    return
  }
  const query: LocationQueryRaw = { ...route.query }
  delete query.sort
  delete query.play
  delete query.account
  delete query.rep
  delete query.connected
  if (next.sort !== DEFAULT_SORT_CHOICE) query.sort = next.sort
  if (next.play) query.play = next.play
  if (next.account) query.account = next.account
  if (next.rep) query.rep = next.rep
  if (next.connected) query.connected = '1'
  void router.push({ query })
}

const salesPlay = computed<string>({
  get: () => (hasPlays.value ? queryStr('play') : ''),
  set: (v) => pushQuery({ play: v }),
})
const accountId = computed<string>({
  get: () => queryStr('account'),
  set: (v) => pushQuery({ account: v }),
})
const repId = computed<string>({
  get: () => queryStr('rep'),
  set: (v) => pushQuery({ rep: v }),
})
const connectedOnly = computed<boolean>({
  get: () => queryStr('connected') === '1',
  set: (v) => pushQuery({ connected: v }),
})
// Derived from the URL-backed sortChoice below.
const sort = computed<DialerHistorySort>(() => sortChoice.value.split(':')[0] as DialerHistorySort)
const direction = computed<'asc' | 'desc'>(() => (sortChoice.value.endsWith(':asc') ? 'asc' : 'desc'))

// Plays and reps are few (tens, bounded by seats), so they stay static option lists fetched
// once. Accounts can run to thousands, so they're searched server-side via `searchAccounts`.
const salesPlays = ref<FilterOption[]>([])
const salesPlayOptions = computed(() => salesPlays.value.map((p) => ({ value: p.id, label: p.name })))
const reps = ref<FilterOption[]>([])
const repOptions = computed(() => {
  const opts = reps.value.map((u) => ({ value: u.id, label: u.name }))
  const m = me.value
  if (m?.user_id && repId.value === m.user_id && !opts.some((o) => o.value === m.user_id)) {
    const name = [m.first_name, m.last_name].filter(Boolean).join(' ').trim()
    opts.push({ value: m.user_id, label: name || m.email })
  }
  return opts
})
const otherRepsHaveCalls = computed(() => reps.value.some((u) => u.id !== repId.value))

const sentinelEl = ref<HTMLElement | null>(null)
let scrollObserver: IntersectionObserver | null = null

// The person key — mirrors the server's pagination key exactly (a linked contact, else
// the bare number), so de-dupe on append can never split or fuse a person.
function rowKey(item: DialerHistoryItem): string {
  return item.contact_id || `num:${item.prospect_e164}`
}

function toggleExpand(key: string) {
  expandedKey.value = expandedKey.value === key ? null : key
}

// Reflect the clicked contact in Raven's right-side context drawer.
function selectContact(item: DialerHistoryItem) {
  if (item.contact_id && item.account_id) {
    setContext({ type: DRAWER_CONTEXT_CONTACT, contactId: item.contact_id, accountId: item.account_id })
  } else if (item.account_id) {
    setContext({ type: DRAWER_CONTEXT_ACCOUNT, accountId: item.account_id })
  }
}

function onToggleRow(item: DialerHistoryItem) {
  const key = rowKey(item)
  const willExpand = expandedKey.value !== key
  toggleExpand(key)
  if (willExpand) selectContact(item)
}

function openDialer() {
  void router.push('/dialer/human')
}

// The sort menu flattens key+direction into one option per reachable pair, so
// the current sort is always readable on the trigger.
const DEFAULT_SORT_CHOICE = 'last_attempt:desc'
const HISTORY_SORT_OPTIONS = [
  { value: DEFAULT_SORT_CHOICE, label: 'Newest attempt first' },
  { value: 'last_attempt:asc', label: 'Oldest attempt first' },
  { value: 'attempts:desc', label: 'Most attempts' },
  { value: 'attempts:asc', label: 'Least attempts' },
  { value: 'contact:asc', label: 'Name (A–Z)' },
  { value: 'contact:desc', label: 'Name (Z–A)' },
]
const VALID_SORT_CHOICES = new Set(HISTORY_SORT_OPTIONS.map((o) => o.value))
const sortChoice = computed<string>({
  // Unrecognized values fall back to the default rather than being passed
  // through to the server.
  get: () => (VALID_SORT_CHOICES.has(queryStr('sort')) ? queryStr('sort') : DEFAULT_SORT_CHOICE),
  set: (v) => pushQuery({ sort: v }),
})

const activeFilterCount = computed(
  () =>
    [salesPlay.value, accountId.value, repId.value].filter(Boolean).length +
    (connectedOnly.value ? 1 : 0),
)

function clearFilters() {
  // One push, one history entry — not four sequential setter writes.
  pushQuery({ play: '', account: '', rep: '', connected: false })
}

// A cursor page still in flight when the filters change must not append rows
// from (or hand back the cursor of) the OLD filter chain — same loadSeq guard
// as useDialerQueue.
let loadSeq = 0
async function load(cursor: string | null = null) {
  const seq = ++loadSeq
  loading.value = true
  try {
    const resp = await getDialerHistory({
      salesPlay: salesPlay.value || undefined,
      accountId: accountId.value || undefined,
      repId: repId.value || undefined,
      connectedOnly: connectedOnly.value,
      sort: sort.value,
      direction: direction.value,
      limit: 50,
      cursor,
    })
    if (seq !== loadSeq) return
    if (!cursor) {
      items.value = resp.items
      // `total` (people) only comes with the first page; cursor pages return null.
      total.value = resp.total ?? 0
    } else {
      // Defensive de-dupe on append (the keyset cursor already prevents overlap; a row
      // whose sort value changed mid-scroll could still repeat).
      const seen = new Set(items.value.map(rowKey))
      items.value = [...items.value, ...resp.items.filter((it) => !seen.has(rowKey(it)))]
    }
    nextCursor.value = resp.next_cursor
  } catch {
    if (seq !== loadSeq) return
    if (!cursor) items.value = []
  } finally {
    if (seq === loadSeq) loading.value = false
  }
}

function loadMore() {
  if (loading.value || !nextCursor.value) return
  void load(nextCursor.value)
}

// Every dropdown is sourced from the history set itself, so no option can return an empty
// page.
async function loadStaticFilterOptions() {
  try {
    const opts = await getDialerHistoryFilterOptions()
    salesPlays.value = opts.salesPlays
    reps.value = opts.reps
  } catch {
    /* leave the previous options in place; the rows still load */
  }
}

// Account search — runs on dropdown open and per (debounced) keystroke. Scoped to the
// selected play and rep, so picking either narrows the accounts on the next search.
async function searchAccounts(query: string) {
  try {
    const opts = await getDialerHistoryFilterOptions({
      salesPlay: salesPlay.value || undefined,
      repId: repId.value || undefined,
      q: query || undefined,
    })
    return opts.accounts.map((a) => ({ value: a.id, label: a.name }))
  } catch {
    return []
  }
}

// Any filter/sort change resets the list to the top (a cursor is only valid for the
// sort/filters it was minted under). Route-backed sources also "change" while
// navigating AWAY — don't fire a farewell fetch.
const mountedPath = route.path
watch([salesPlay, accountId, repId, connectedOnly, sort, direction], () => {
  if (route.path !== mountedPath) return
  expandedKey.value = null
  void load()
})

function setupScrollObserver() {
  scrollObserver?.disconnect()
  if (sentinelEl.value) {
    scrollObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore()
      },
      { rootMargin: '200px', threshold: 0 },
    )
    scrollObserver.observe(sentinelEl.value)
  }
}

onMounted(async () => {
  // `_dismissed` is a global singleton defaulting to open, so dropping the
  // openDrawer() call is not enough on its own — the first setContext would
  // still render the panel expanded. Park it collapsed.
  closeDrawer()
  // Warm the CRM deep-link config (Salesforce base URL, HubSpot portal id, …) so the
  // in-row logo links resolve. Idempotent + shared with the dialer page.
  void preloadCrmLinkConfig()
  // Play + rep options and the first page in parallel; accounts load on dropdown open.
  void loadStaticFilterOptions()
  await load()
  await nextTick()
  setupScrollObserver()
})

onBeforeUnmount(() => {
  scrollObserver?.disconnect()
  openDrawer()
})
</script>

<style scoped>
.eyebrow {
  font-family: var(--font-sans);
  font-size: var(--fs-xs, 12px);
  font-weight: var(--fw-bold, 700);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wide, 0.06em);
  color: var(--color-text-tertiary);
}
.totalBadge {
  margin-left: var(--space-2, 8px);
  font-size: 11px;
  font-weight: 700;
  color: var(--color-text-tertiary);
  background: var(--color-bg-app);
  border-radius: var(--radius-full);
  padding: 1px 7px;
}
.totalCount {
  font-size: var(--fs-sm);
  color: var(--color-text-tertiary);
  font-variant-numeric: tabular-nums;
}
.openDialerBtn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

/* On narrow / embedded widths the 7 columns can't all fit; scroll horizontally
   instead of clipping the right-hand columns. Height stays auto so the page's own
   vertical scroll (PageChrome) is unaffected. */
.histScroll {
  overflow-x: auto;
}
/* Shared column template — inherited by the header here and by each DialerHistoryRow.
   min-width is the readable floor; below it .histScroll scrolls. */
.histTable {
  /* The sum of the track minimums + gaps + row padding. Below this .histScroll scrolls
     rather than squeezing tracks under their minimum. */
  min-width: 1044px;
  --hist-grid: 26px minmax(160px, 1.4fr) minmax(120px, 1fr) minmax(110px, 0.95fr) 78px minmax(170px, 1fr) minmax(120px, 1fr) 150px;
}
/* Playless tenants: the same grid without the Sales-play column (one 110px track + one gap less). */
.histTable.noPlays {
  min-width: 922px;
  --hist-grid: 26px minmax(160px, 1.4fr) minmax(120px, 1fr) 78px minmax(170px, 1fr) minmax(120px, 1fr) 150px;
}
.histHead {
  display: grid;
  grid-template-columns: var(--hist-grid);
  gap: 12px;
  align-items: center;
  padding: 6px 12px;
  background: var(--color-bg-surface, #fff);
}
.colLabel {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-text-tertiary, #a3a3a3);
  /* Headers elide like the cells do rather than running into the next column. */
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rows {
  display: flex;
  flex-direction: column;
}
.rowBlock {
  display: flex;
  flex-direction: column;
}

.scrollSentinel {
  height: 1px;
}
.loadingMore {
  text-align: center;
  font-size: 12px;
  color: var(--color-text-tertiary);
  padding: 8px;
}
.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: var(--space-8, 56px);
}
.emptyText {
  font-size: 14px;
  color: var(--color-text-tertiary, #a3a3a3);
}
.emptyHint {
  font-size: 13px;
  color: var(--color-text-tertiary, #a3a3a3);
  opacity: 0.85;
}
</style>
